import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import { API_URL } from '../../config';
import './NotificationBell.css';

function formatNotificationTime(iso) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'Yesterday';
  if (day < 7) return `${day} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SeverityIcon({ severity }) {
  if (severity === 'critical') {
    return (
      <span className="notification-item__icon notification-item__icon--critical" aria-hidden>
        <AlertCircle size={18} strokeWidth={2.25} />
      </span>
    );
  }
  if (severity === 'positive') {
    return (
      <span className="notification-item__icon notification-item__icon--positive" aria-hidden>
        <CheckCircle size={18} strokeWidth={2.25} />
      </span>
    );
  }
  return (
    <span className="notification-item__icon notification-item__icon--warning" aria-hidden>
      <AlertTriangle size={18} strokeWidth={2.25} />
    </span>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  /** Parse GET /api/notifications body: { notifications, unreadCount } */
  const applyFeedResponse = useCallback((res) => {
    const payload = res?.data;
    const nextList = Array.isArray(payload?.notifications) ? payload.notifications : [];
    setNotifications(nextList);
    const u = Number(payload?.unreadCount);
    setUnreadCount(Number.isFinite(u) ? u : 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }
    setLoading(true);
    axios
      .get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) applyFeedResponse(res);
      })
      .catch(() => {
        if (!cancelled) {
          setNotifications([]);
          setUnreadCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applyFeedResponse]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }
    axios
      .get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) applyFeedResponse(res);
      })
      .catch(() => {
        if (!cancelled) {
          setNotifications([]);
          setUnreadCount(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, applyFeedResponse]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const toggle = () => {
    setOpen((v) => !v);
  };

  const markAllRead = async (e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await axios.patch(`${API_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      applyFeedResponse(res);
    } catch {
      /* ignore */
    }
  };

  const onItemClick = async (n) => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await axios.patch(`${API_URL}/api/notifications/${n.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        /* still navigate */
      }
    }
    setOpen(false);
    const path = n.route && n.route.startsWith('/') ? n.route : '/checklists';
    navigate(path);
    if (token) {
      try {
        const res = await axios.get(`${API_URL}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        applyFeedResponse(res);
      } catch {
        /* ignore */
      }
    }
  };

  const goAllAlerts = () => {
    setOpen(false);
    navigate('/alerts');
  };

  const list = notifications ?? [];
  const showMarkAll = list.some((n) => !n.isRead);

  return (
    <div className="notification-bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className="topbar__icon-btn"
        onClick={toggle}
        aria-label="Notifications"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
      {open && (
        <div className="notification-dropdown" role="dialog" aria-label="Notifications">
          <div className="notification-dropdown__header">
            <h2 className="notification-dropdown__title">Notifications</h2>
            {showMarkAll && (
              <button type="button" className="notification-dropdown__mark-all" onClick={markAllRead}>
                Mark all as read
              </button>
            )}
          </div>
          <div className="notification-dropdown__body">
            {loading && list.length === 0 ? (
              <p className="notification-dropdown__empty">Loading…</p>
            ) : list.length === 0 ? (
              <p className="notification-dropdown__empty">You&apos;re up to date. No new alerts.</p>
            ) : (
              list.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`notification-item${n.isRead ? '' : ' notification-item--unread'}`}
                  onClick={() => onItemClick(n)}
                >
                  <SeverityIcon severity={n.severity} />
                  <div className="notification-item__main">
                    <p className="notification-item__title">{n.title}</p>
                    <p className="notification-item__subtitle">{n.subtitle}</p>
                    <p className="notification-item__time">{formatNotificationTime(n.createdAt)}</p>
                  </div>
                  {!n.isRead && <span className="notification-item__dot" aria-label="Unread" />}
                </button>
              ))
            )}
          </div>
          <div className="notification-dropdown__footer">
            <button type="button" className="notification-dropdown__view-all" onClick={goAllAlerts}>
              View all alerts →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
